import CheckIcon from '@heroicons/react/24/outline/CheckIcon';
import MinusIcon from '@heroicons/react/24/outline/MinusIcon';

import { Button } from '@/components/core/Button';
import { Toggle } from '@/components/core/Toggle';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

interface DamTagAssignmentRowProps {
    name: string;
    /** Selected assets that carry the tag after the pending changes. */
    assignedCount: number;
    /** Number of selected assets. */
    total: number;
    /** All carry the tag → remove it from all; otherwise → assign it to all. */
    onToggle: () => void;
    onAssignAll: () => void;
}

/**
 * Tag row of the bulk tag dialog (wireframe `…;7514:316212`): the tag as a pill that
 * toggles its assignment, "(k of n assigned)" for several assets and "Assign all" while
 * only some of the selected assets carry it. `aria-pressed` is `mixed` for a partial
 * assignment.
 */
function DamTagAssignmentRow({ name, assignedCount, total, onToggle, onAssignAll }: DamTagAssignmentRowProps) {
    const { t } = useTranslation();
    const isAll = total > 0 && assignedCount === total;
    const isPartial = assignedCount > 0 && !isAll;

    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-py-1">
            <Toggle
                pressed={isAll}
                aria-pressed={isPartial ? 'mixed' : isAll}
                onPressedChange={onToggle}
                className={cn(
                    'tw-h-auto tw-gap-1 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-py-1 tw-text-left',
                    'tw-text-t-sm tw-font-normal tw-text-t-primary tw-transition-colors tw-duration-100 tw-ease-in-out lg:tw-text-t-base',
                    assignedCount > 0
                        ? 'tw-border-primary-active tw-bg-fill-secondary hover:tw-bg-fill-secondary-hover'
                        : 'tw-border-primary tw-bg-fill-primary hover:tw-bg-fill-primary-hover',
                )}
            >
                {isAll && <CheckIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0 tw-icon-primary" />}
                {isPartial && <MinusIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0 tw-icon-primary" />}
                <span className="tw-break-words">{name}</span>
            </Toggle>
            {assignedCount > 0 && total > 1 && (
                <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                    {t('DAM.TAGS.ASSIGNED_COUNT', { count: assignedCount.toLocaleString(), total: total.toLocaleString() })}
                </span>
            )}
            {isPartial && (
                <Button variant="ghost" onClick={onAssignAll} className="tw-underline">
                    {t('DAM.TAGS.ASSIGN_ALL')}
                </Button>
            )}
        </div>
    );
}

export { DamTagAssignmentRow };
export type { DamTagAssignmentRowProps };
